import format from 'date-fns/format'
import parseISO from 'date-fns/parseISO'
import {
  type ComponentType,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  buildDestinationCategoryFromDrupalId,
  defaultConfig,
  SearchForm as DefaultSearchForm,
  SearchFormConfig,
  SearchFormProps as DesignSystemSearchFormProps,
} from '@awazevr/design-system.compositions.search-form'
import { SearchForm as DriveTimeSearchForm } from '@awazevr/design-system.compositions.search-form.drivetime'
import {
  useSuggestions,
  useFetchDestination,
  useFetchDestinationForLegacyList,
  useResolveGooglePlacesID,
  GoogleSearchMethods,
} from '@awazevr/mfe-common.api.places-api'
import { TmsContext } from '@awazevr/mfe-common.contexts.tms'
import { generateSearchUrl } from '@awazevr/mfe-common.utils.search-url'
import { Brand } from '@awazevr/design-system.components.awaze-theme-provider'
import { AutoCompleteResult } from '@awazevr/design-system.components.autocomplete'
import { locationLookupClient } from '@awazevr/mfe-common.api.location-lookup-client'
import { GlobalProps } from 'src/utils/globalProps'
import { addBoatsToDestinationList } from 'src/lib/contentful/boats-options-data'

import {
  FUSION_DATE_FORMAT,
  FormData,
  SearchFormProps,
  DEFAULT_SEARCH_CRITERIA,
  PlacesSuggestions,
  Suggestion,
} from './SearchForm.types'
import {
  getBaseUrl,
  getCurrentPublicRuntimeConfig,
} from '../../utils/helpers/helpers'
import { sendDataLayerSearchSubmitEvent } from '../../utils/dataLayer/searchDataLayer'
import {
  closeSearchformDrawers,
  formatAutocompleteSubtitle,
  getAccommodationType,
  getHardcodedDestinationData,
  getUrlForEnv,
} from './SearchForm.helper'
import { useOptimizelyPlacesJson } from '../../lib/optimizely/experiments/use-places-config'
import { useSearchformTranslations } from './searchform-translations'
import { useSearchFormFreeDateSelection } from '../../lib/optimizely/experiments/use-search-form-free-date-selection'
import {
  trackOptimizelyClientsideEvent,
  useSpecificOptimizelyUserDecision,
} from '../../lib/optimizely/optimizely-for-client'
import {
  OPTIMIZELY_EVENTS,
  OPTIMIZELY_FLAGS_ENUM,
  DEFAULT_MAX_STAY_DURATION,
  UK_MAX_STAY_DURATION,
} from '../../constants'
import { getDriveTimeRadiusKm } from 'src/utils/getDriveTimeRadiusKm'
import { newrelicErrorHandler } from 'src/utils/errorHandler/newrelicErrorHandler'
import { useGoogleFallback } from 'src/lib/optimizely/experiments/use-google-fallback'
import { useRegionVsPlacesExp } from 'src/lib/optimizely/experiments/use-region-vs-places'
import { useSearchSelectionEvent } from './useSearchSelectionEvent'
import { handleNearbyPropertiesClick } from './SearchForm.utils.drivetime'
import { combineAutoSuggestSearch } from './combineAutoSuggestSearch'

/**
 * Function to Build the search url based on environment and parameters.
 */
export const buildSearchUrl = (
  formData: FormData,
  brand: Brand,
  config: SearchFormConfig,
  radiusCentre?: string | null,
  radiusKm?: number,
): string => {
  const {
    id,
    start,
    destination,
    destinationCategory,
    destinationId,
    accommodationType,
    destinationURL,
    infant,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    radiusCentre: _radiusCentre,
    ...rest
  } = formData

  const baseURL = getBaseUrl(brand)

  // Is Places-ID search if 'id' is empty (naked search) or a Number
  // Prevents Google GUID and radiusCentre from being added to query as these are not reset
  const isPlacesId = (!id?.length || !Number.isNaN(Number(id))) && !radiusCentre

  const params = {
    regionName: destination,
    ...(isPlacesId && !destinationURL && id?.length && { placesId: id }),
    start: start && format(parseISO(start), FUSION_DATE_FORMAT),
    infant: config.infants.max > 0 && infant,
    accommodationType:
      ['cottages.com', 'hoseasons'].indexOf(brand) !== -1 &&
      (accommodationType || DEFAULT_SEARCH_CRITERIA.accommodationType),
    ...(!isPlacesId && radiusCentre && { radiusCentre }),
    ...(radiusCentre && radiusKm && { radiusKm }),
    // Is ONLY used for Accommodation-Type: BOATS
    ...(destinationId && { regionId: destinationId }),
    ...(destinationCategory && { destinationCategory }),
    ...(destinationURL && { destinationURL }),
    ...rest,
  }

  return `${baseURL}${generateSearchUrl(params, brand)}`
}

const getAutoSuggestSearch = (brand: Brand, marketCode: string) => {
  const publicRuntimeConfig = getCurrentPublicRuntimeConfig()

  return async (
    searchTerm: string,
    accommodationType: string,
    propertiesOnly: boolean,
  ) => {
    const locationResult = await locationLookupClient(searchTerm, {
      brand: brand,
      language: marketCode,
      accommodationType: accommodationType,
      maxAutocompleteResults: 100,
      auth: {
        LOCATION_API_URL: getUrlForEnv(publicRuntimeConfig?.APP_ENV),
        LOCATION_API_KEY: publicRuntimeConfig?.LOCATION_API_KEY,
      },
    })

    const mapPropertyToAutocomplete = (p) =>
      ({
        id: p.id,
        link: p.link,
        name: p.title,
        subtitle: [p.id, p.location].join(', ').replace(/,\s*$/, ''),
        property: true,
      } as AutoCompleteResult)

    return propertiesOnly
      ? locationResult
          .filter((p) => p.property && 'property' in p && 'title' in p)
          .map((p) => mapPropertyToAutocomplete(p))
      : locationResult.map((l) =>
          l.property && 'property' in l && 'title' in l
            ? mapPropertyToAutocomplete(l)
            : (l as AutoCompleteResult),
        )
  }
}

export const usePlacesSuggestions = (
  googleSearchMethod: GoogleSearchMethods,
): PlacesSuggestions => {
  const getSuggestions = useSuggestions()

  return async (searchTerm: string) => {
    try {
      const placesResult = (
        await getSuggestions(searchTerm, 100, googleSearchMethod)
      ).map((location) => ({
        ...location,
        type: location.type as Suggestion['type'],
        subtitle: formatAutocompleteSubtitle(location),
        link: '',
      }))

      return placesResult
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(
        'Error while calling places',
        e instanceof Error ? e.message : e,
      )
      return []
    }
  }
}

type SearchFormWithNearbyProps = DesignSystemSearchFormProps & {
  onNearbyPropertiesClick?: () => Promise<unknown>
  onNearbyPropertiesSeen?: () => void
}

export const SearchFormComponent: React.FC<SearchFormProps> = ({
  config = defaultConfig,
  isSummary = false,
  placesConfig = {
    useLegacyDestinationSelector: false,
    initialParentDestinationList: null,
    pageSize: 20,
    pageSizePopular: 20,
    googleFallbackMethod: undefined,
  },
}) => {
  useEffect(() => {
    window.addEventListener('UC_UI_CMP_EVENT', (data) => {
      closeSearchformDrawers(data as Event & { detail })
    })
    return () => {
      window.removeEventListener('UC_UI_CMP_EVENT', closeSearchformDrawers)
    }
  }, [])
  const [searchFormClicked, setSearchFormClicked] = useState(false)
  const {
    brand,
    contentfulLocale,
    country,
    marketCode,
    slug,
    optimizelySdkKey,
    host,
    awazeClient,
    pageType,
  } = useContext(GlobalProps)
  const tms = useContext(TmsContext)
  const driveTimeDecision = useSpecificOptimizelyUserDecision(
    OPTIMIZELY_FLAGS_ENUM.DRIVE_TIME_SEARCH,
  )
  const driveTimeSearchEnabled = driveTimeDecision?.variationKey === 'variant_a'
  const hasTrackedNearbySeen = useRef(false)

  const {
    handleAutoSuggestStart,
    handleDestinationSelection,
    handleDateSelection,
    handleGuestSelection,
    handleSearchSelectionStart,
    handleSearchSelectionClose,
  } = useSearchSelectionEvent()

  const publicRuntimeConfig = getCurrentPublicRuntimeConfig()
  placesConfig.useLegacyDestinationSelector =
    publicRuntimeConfig?.[brand]?.useLegacyDestinationSelector || false

  // XP-1396 Determine which Google fallback method to use for the given brand
  placesConfig.googleFallbackMethod = useGoogleFallback(brand)

  // Modify the placesConfig based on the Optimizely flags
  placesConfig = useOptimizelyPlacesJson(brand, contentfulLocale, placesConfig)

  const placesVsRegionXPVariant = useRegionVsPlacesExp()

  const exp2800VarsWithoutGoogleFallback =
    placesVsRegionXPVariant === 'variant_a' ||
    placesVsRegionXPVariant === 'variant_b'

  if (exp2800VarsWithoutGoogleFallback) {
    placesConfig.googleFallbackMethod = null
  }

  const {
    useLegacyDestinationSelector,
    initialParentDestinationList,
    pageSize,
    pageSizePopular,
    googleFallbackMethod,
  } = placesConfig

  // Places-API Google Coordinates (radiusCentre) fetch
  const [googleID, setGoogleID] = useState('')
  const radiusCentre = useResolveGooglePlacesID(googleID)

  // Places-API legacy-list (old)design data
  const [fetchPlacesLegacyList, setFetchPlacesLegacyList] = useState(false)
  const placesLegacyListData = useFetchDestinationForLegacyList(
    fetchPlacesLegacyList,
  )

  useEffect(() => {
    setFetchPlacesLegacyList(searchFormClicked && useLegacyDestinationSelector)
  }, [searchFormClicked])

  const freeDateSelectionEnabled =
    useSearchFormFreeDateSelection(searchFormClicked)

  if (brand === 'hoseasons') {
    addBoatsToDestinationList(config)
  }

  const getSuggestions = usePlacesSuggestions(googleFallbackMethod)
  const originalAutoSuggestSearch = getAutoSuggestSearch(brand, marketCode)

  const getDestination = useFetchDestination()
  const SearchFormForVariant = (
    driveTimeSearchEnabled ? DriveTimeSearchForm : DefaultSearchForm
  ) as ComponentType<SearchFormWithNearbyProps>

  const predeterminedPlacesId = config?.placesId
  const [
    predeterminedDestinationCategory,
    setPredeterminedDestinationCategory,
  ] = useState<string | undefined>()

  useEffect(() => {
    setPredeterminedDestinationCategory(undefined)
    if (!predeterminedPlacesId) return
    let cancelled = false
    const loadPredeterminedDestinationCategory = async () => {
      try {
        const destination = await getDestination(
          predeterminedPlacesId.toString(),
          false,
        )
        if (cancelled) return
        const category = buildDestinationCategoryFromDrupalId(
          destination?.drupalId,
        )
        if (category) setPredeterminedDestinationCategory(category)
      } catch (error) {
        newrelicErrorHandler(error)
      }
    }
    loadPredeterminedDestinationCategory()
    return () => {
      cancelled = true
    }
  }, [predeterminedPlacesId])

  const memoizedInitialValues = useMemo(
    () => ({
      destination: config?.destination?.defaultValue || '',
      destinationURL: config?.destinationURL?.defaultValue || '',
      ...(config?.accommodationType && {
        accommodationType: getAccommodationType(brand, slug, config),
      }),
    }),
    [
      config?.destination?.defaultValue,
      config?.destinationURL?.defaultValue,
      config?.accommodationType,
      brand,
      slug,
    ],
  )

  const handleNearbyPropertiesSeen = () => {
    if (hasTrackedNearbySeen.current) {
      return
    }

    hasTrackedNearbySeen.current = true

    trackOptimizelyClientsideEvent(
      optimizelySdkKey,
      OPTIMIZELY_EVENTS.NEARBY_PROPERTIES_SEEN,
      brand,
      contentfulLocale,
      host,
      awazeClient,
    )
  }

  const trackSearchSuggestionOptimizelyEvent = (
    eventKey: string,
    eventTags?: Record<string, string | number>,
  ) => {
    trackOptimizelyClientsideEvent(
      optimizelySdkKey,
      eventKey,
      brand,
      contentfulLocale,
      host,
      awazeClient,
      undefined,
      eventTags,
    )
  }

  const handleDestinationSelectionWithOptimizely: DesignSystemSearchFormProps['onDestinationSelectionEvent'] =
    (payload) => {
      handleDestinationSelection?.(payload)

      if (payload.source !== 'places-api') {
        return
      }

      trackSearchSuggestionOptimizelyEvent(
        OPTIMIZELY_EVENTS.INTERNAL_LOCATION_SUGGESTION_SELECTED,
        payload.autoSuggestMeta
          ? { position: payload.autoSuggestMeta.position }
          : undefined,
      )
    }

  return (
    <div
      onClick={() => {
        if (!searchFormClicked) {
          setSearchFormClicked(() => true)
        }
      }}
    >
      <SearchFormForVariant
        defaultConfig={{
          ...config,
          showDurationInput: !freeDateSelectionEnabled,
          autocompleteDebounceWait: 600,
          maxAmountOfNights:
            freeDateSelectionEnabled &&
            brand !== 'novasol' &&
            brand !== 'jamesVillas' &&
            brand !== 'fincallorca'
              ? UK_MAX_STAY_DURATION
              : DEFAULT_MAX_STAY_DURATION,
          destinationListConfig: {
            ...(initialParentDestinationList && {
              initialParent: initialParentDestinationList,
            }),
            destinationFetch: async (locale, id, accommodationType) => {
              id = id?.toString()
              const destinationTimerStart = new Date().getTime()
              const destinationId = id === 'countries' ? undefined : id
              const destinations =
                accommodationType !== 'boat-holidays'
                  ? getDestination(destinationId, true)
                  : getHardcodedDestinationData(locale, id, brand)

              const result = await destinations
              const destinationTimerEnd = new Date().getTime()

              if (
                typeof window !== 'undefined' &&
                typeof window.newrelic !== 'undefined'
              ) {
                window.newrelic.addPageAction('places-api-speed', {
                  type: 'DestinationList',
                  action: `Clicked: ${result.name}`,
                  time: destinationTimerEnd - destinationTimerStart,
                })
              }

              return destinations
            },
            pageSize,
            pageSizePopular,
            fetchDelay: 0,
            sortAlphabetically: true,
          },
        }}
        autoSuggestSearch={combineAutoSuggestSearch(
          async (searchTerm) => {
            const timerStart = new Date().getTime()
            const result = await getSuggestions(searchTerm)
            const timerEnd = new Date().getTime()

            if (
              typeof window !== 'undefined' &&
              typeof window.newrelic !== 'undefined'
            ) {
              window.newrelic.addPageAction('places-api-speed', {
                type: 'Autocomplete',
                action: `Searched for: ${searchTerm}`,
                time: timerEnd - timerStart,
              })
            }

            return result
          },
          originalAutoSuggestSearch,
          tms,
          trackSearchSuggestionOptimizelyEvent,
        )}
        onSubmit={(formData: FormData) => {
          const { nights, accommodationType } = formData
          delete formData.featureId

          // if NOT Accommodation-Type Boats remove RegionID/destinationURL
          if (accommodationType !== 'boat-holidays') {
            delete formData.destinationId
            delete formData.destinationURL
          }
          const dayDurationEventKey = `DAY_DURATION_SELECTION_${nights}`
          const formDataWithDestinationURL = {
            ...formData,
            id: formData.id ?? config.placesId,
            destinationCategory:
              formData.destinationCategory || predeterminedDestinationCategory,
          }
          const resolvedRadiusCentre = formData.radiusCentre || radiusCentre

          sendDataLayerSearchSubmitEvent(
            {
              ...formDataWithDestinationURL,
              ...(googleID && {
                googleSearch: `google places - ${placesConfig.googleFallbackMethod}`,
              }),
            },
            newrelicErrorHandler,
            tms,
          )

          const url = buildSearchUrl(
            formDataWithDestinationURL,
            brand,
            config,
            resolvedRadiusCentre,
            formData.radiusCentre
              ? getDriveTimeRadiusKm({ brand, country })
              : undefined,
          )

          if (pageType === 'Homepage') {
            trackOptimizelyClientsideEvent(
              optimizelySdkKey,
              OPTIMIZELY_EVENTS.HP_SEARCH_SUBMIT,
              brand,
              contentfulLocale,
              host,
              awazeClient,
            )
          }

          if (formData.radiusCentre) {
            trackOptimizelyClientsideEvent(
              optimizelySdkKey,
              OPTIMIZELY_EVENTS.NEARBY_PROPERTIES_SEARCH_FORM_SUBMITTED,
              brand,
              contentfulLocale,
              host,
              awazeClient,
            )
          }

          if (
            freeDateSelectionEnabled &&
            OPTIMIZELY_EVENTS[dayDurationEventKey]
          ) {
            trackOptimizelyClientsideEvent(
              optimizelySdkKey,
              OPTIMIZELY_EVENTS[dayDurationEventKey],
              brand,
              contentfulLocale,
              host,
              awazeClient,
            )
          }

          window.location.assign(url)
        }}
        translations={useSearchformTranslations(driveTimeSearchEnabled)}
        isSummary={isSummary}
        locale={contentfulLocale}
        initialValues={memoizedInitialValues}
        useLegacyDestinationSelector={useLegacyDestinationSelector}
        legacyListWithPlaces={{
          enabled: useLegacyDestinationSelector,
          data: placesLegacyListData,
        }}
        onDestinationInputStart={handleAutoSuggestStart}
        onAutocompleteSelected={(destinationId, source) =>
          ['google-textsearch', 'google-autocomplete'].includes(source)
            ? setGoogleID(destinationId)
            : null
        }
        {...(driveTimeSearchEnabled
          ? {
              onNearbyPropertiesClick: () =>
                handleNearbyPropertiesClick({
                  warningLogger: (message) => {
                    globalThis.window?.newrelic?.noticeError?.(message)
                  },
                  sendOptimizelyEvent: (eventKey) => {
                    trackOptimizelyClientsideEvent(
                      optimizelySdkKey,
                      eventKey,
                      brand,
                      contentfulLocale,
                      host,
                      awazeClient,
                    )
                  },
                }),
              onNearbyPropertiesSeen: handleNearbyPropertiesSeen,
            }
          : {})}
        onDestinationSelectionEvent={handleDestinationSelectionWithOptimizely}
        onDateSelectionEvent={handleDateSelection}
        onGuestSelectionEvent={handleGuestSelection}
        onSearchSelectionStart={handleSearchSelectionStart}
        onSearchSelectionClose={handleSearchSelectionClose}
      />
    </div>
  )
}
